import { useEffect, useRef } from 'react';
import { prefersReducedMotion } from './motion';

/* Perspective-projected field converging inward. */
export default function StreamCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const reduced = prefersReducedMotion();
    let animFrame;
    let viewW = 0;
    let viewH = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      viewW = canvas.offsetWidth;
      viewH = canvas.offsetHeight;
      canvas.width = Math.max(1, Math.round(viewW * dpr));
      canvas.height = Math.max(1, Math.round(viewH * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('orientationchange', resize);

    const NEAR = 0.32;
    const FAR = 2.6;
    const makeParticle = depth => ({
      x: (Math.random() - 0.5) * 2.6,
      y: (Math.random() - 0.5) * 2.6,
      z: depth ?? NEAR + Math.random() * (FAR - NEAR),
      speed: 0.0022 + Math.random() * 0.0042,
      size: 0.7 + Math.random() * 1.5,
      teal: Math.random() > 0.45,
    });

    const particles = Array.from({ length: 64 }, () => makeParticle());
    const projected = new Array(particles.length);

    const draw = () => {
      if (viewW === 0 || viewH === 0) resize();
      const width = viewW;
      const height = viewH;
      const cx = width / 2;
      const cy = height / 2;
      const spread = Math.min(width, height) * 0.62;

      ctx.fillStyle = 'rgba(237,228,216,0.9)';
      ctx.fillRect(0, 0, width, height);

      for (let index = 0; index < particles.length; index += 1) {
        const particle = particles[index];
        if (!reduced) particle.z += particle.speed;
        if (particle.z > FAR) Object.assign(particle, makeParticle(NEAR));

        const scale = 1 / particle.z;
        projected[index] = {
          sx: cx + particle.x * spread * scale,
          sy: cy + particle.y * spread * scale,
          radius: particle.size * scale * 1.5,
          depth: (FAR - particle.z) / (FAR - NEAR),
          teal: particle.teal,
        };
      }

      for (let index = 0; index < projected.length; index += 1) {
        const point = projected[index];
        for (let other = index + 1; other < projected.length; other += 1) {
          const partner = projected[other];
          const dx = point.sx - partner.sx;
          const dy = point.sy - partner.sy;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance > 132) continue;
          ctx.globalAlpha = (1 - distance / 132) * 0.16 * Math.min(point.depth, partner.depth);
          ctx.beginPath();
          ctx.moveTo(point.sx, point.sy);
          ctx.lineTo(partner.sx, partner.sy);
          ctx.strokeStyle = '#1f46c9';
          ctx.lineWidth = 0.7;
          ctx.stroke();
        }
      }

      projected.forEach(point => {
        ctx.globalAlpha = 0.2 + point.depth * 0.6;
        ctx.beginPath();
        ctx.arc(point.sx, point.sy, Math.max(0.4, point.radius), 0, Math.PI * 2);
        ctx.fillStyle = point.teal ? '#1f46c9' : '#3f74e6';
        ctx.fill();
      });

      ctx.globalAlpha = 1;
      const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, spread * 0.42);
      glow.addColorStop(0, 'rgba(31,70,201,0.20)');
      glow.addColorStop(1, 'rgba(31,70,201,0)');
      ctx.beginPath();
      ctx.arc(cx, cy, spread * 0.42, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();

      animFrame = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animFrame);
      window.removeEventListener('resize', resize);
      window.removeEventListener('orientationchange', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="stream-canvas" />;
}
